import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { IonicVue } from '@ionic/vue';
import App from './App.vue';
import router from './router';

/* Ionic core CSS */
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* SwapWish theme (dark + viola) */
import './theme/variables.css';
import './theme/common.css';

const AUTH_STORAGE_KEY = 'swapwish.auth';
const FAST_PUBLIC_PATHS = new Set([
  '/',
  '/landing',
  '/privacy',
  '/delete-account',
  '/child-safety',
  '/terms',
]);

function hasPersistedAuthSession(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;

    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed;
    return !!(session && (session.access_token || session.refresh_token));
  } catch {
    return false;
  }
}

function canDeferUserInitForInitialRoute(): boolean {
  if (typeof window === 'undefined') return false;
  if (hasPersistedAuthSession()) return false;

  const path = window.location.pathname || '/';
  return FAST_PUBLIC_PATHS.has(path) || path.startsWith('/invite/') || path.startsWith('/s/');
}


async function settleWithin<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => {
          console.warn(`[bootstrap] ${label} timed out after ${timeoutMs}ms`);
          resolve(null);
        }, timeoutMs);
      }),
    ]);
  } catch (error) {
    console.error(`[bootstrap] ${label}`, error);
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Bootstrap registers the auth listener and reads the cached session from
// localStorage synchronously. Profile and plan load in the background.
(async () => {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(IonicVue, { mode: 'md' });

  const { vSwipeDownClose } = await import('./directives/swipeDownClose');
  app.directive('swipe-down-close', vSwipeDownClose);

  if (canDeferUserInitForInitialRoute()) {
    app.use(router);
    await settleWithin(router.isReady(), 8000, 'router ready');
    app.mount('#app');

    void import('./stores/useUserStore')
      .then(({ useUserStore }) => useUserStore().init({ blockingUserData: false }))
      .catch((error) => {
        console.error('[bootstrap] user init', error);
      });
  } else {
    const { useUserStore } = await import('./stores/useUserStore');
    const userInitPromise = useUserStore().init({ blockingUserData: false });
    await settleWithin(userInitPromise, 2500, 'user init');

    app.use(router);
    await settleWithin(router.isReady(), 8000, 'router ready');
    app.mount('#app');
  }
})();
